import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useNavigate, Navigate, useLocation } from 'react-router-dom';
import LegalFooter from './components/LegalFooter';
import RequireAuth from './components/RequireAuth';
import TenantLayout from './components/TenantLayout';
import RootRedirect from './components/RootRedirect';
import { useTenantFromUrl } from './hooks/useTenantFromUrl';
import ClientList from './components/ClientList';
import ScAssignmentList from './components/ScAssignmentList';
import ClientForm from './components/ClientForm';
import ClientDocuments from './components/ClientDocuments';
import ClientDocumentFolder from './components/ClientDocumentFolder';
import ClientLayout from './components/ClientLayout';
import { ClientAtPackets, ClientAtPacketCreate, ClientFileReviews, ClientServices, ClientDocumentsAll, ClientMilestones, ClientTeam, ClientChecklists, ClientWorkTasks } from './components/ClientEmbeddedViews';
import ClientEmailTemplates from './components/ClientEmailTemplates';
import ProviderList from './components/ProviderList';
import ProviderForm from './components/ProviderForm';
import PayeeList from './components/PayeeList';
import PayeeForm from './components/PayeeForm';
import ContactList from './components/ContactList';
import ContactForm from './components/ContactForm';
import ContactRelatedClients from './components/ContactRelatedClients';
import SchoolList from './components/SchoolList';
import SchoolForm from './components/SchoolForm';
import ReportsHub from './pages/ReportsHub';
import ClientsReport from './pages/ClientsReport';
import ClientStatusQuickEdit from './pages/ClientStatusQuickEdit';
import MonthlyContact from './pages/MonthlyContact';
import ReportPageWrapper from './components/ReportPageWrapper';
import WorkTasksDashboard from './components/WorkTasksDashboard';
import MilestoneRules from './pages/MilestoneRules';
import ReferralForm from './components/ReferralForm';
import ClientReferralInsurancePage from './components/ClientReferralInsurancePage';
import ReferralList from './components/ReferralList';
import ReferralMilestones from './pages/ReferralMilestones';
import ReferralChecklists from './pages/ReferralChecklists';
import MyChecklists from './pages/MyChecklists';
import PersonalMyTodos from './pages/PersonalMyTodos';
import CreateClientReferral from './pages/CreateClientReferral';
import IncompleteToCornerstone from './pages/IncompleteToCornerstone';
import CornerstoneDetail from './pages/CornerstoneDetail';
import LookupTableManager from './pages/LookupTableManager';
import SettingsLayout from './components/SettingsLayout';
import ListsLayout from './components/ListsLayout';
import MainLayout from './components/MainLayout';
import ClientFolderTemplate from './pages/ClientFolderTemplate';
import ChecklistTemplates from './pages/ChecklistTemplates';
import FileReviewTemplate from './pages/FileReviewTemplate';
import RolePermissionManager from './pages/RolePermissionManager';
import TenantSettings from './pages/TenantSettings';
import NotificationTemplates from './pages/NotificationTemplates';
import BillableSeatsPage from './pages/BillableSeatsPage';
import EmailTemplates from './pages/EmailTemplates';
import AutomationRules from './pages/AutomationRules';
import RequireSuperAdmin from './components/RequireSuperAdmin';
import ResetUserPassword from './pages/ResetUserPassword';
import TenantTeam from './pages/TenantTeam';
import TestTools from './pages/TestTools';
import AtPackets from './pages/AtPackets';
import AtPacketsPendingReceipt from './pages/AtPacketsPendingReceipt';
import SupportCases from './pages/SupportCases';
import SupportCaseCreate from './pages/SupportCaseCreate';
import SupportCaseDetail from './pages/SupportCaseDetail';
import { SupportConsoleTab, SupportConsoleDetail } from './pages/SupportConsole';
import { SuperAdminReleasesTab } from './pages/SuperAdminReleasesTab';
import AtPacketCreate from './pages/AtPacketCreate';
import AtPacketDetail from './pages/AtPacketDetail';
import FileReviewList from './pages/FileReviewList';
import FileReviewDetail from './pages/FileReviewDetail';
import FileReviewCreate from './pages/FileReviewCreate';
import TransferList from './pages/TransferList';
import TransferDetail from './pages/TransferDetail';
import TrainingList from './pages/TrainingList';
import DataCorrectionsList from './pages/DataCorrectionsList';
import ActingScCoveragePage from './pages/ActingScCoveragePage';
import NotifyStaff from './pages/NotifyStaff';
import MailingListBuilder from './pages/MailingListBuilder';
import ProviderSearch from './pages/ProviderSearch';
import DocGenLanding from './pages/DocGenLanding';
import DocGenTemplatesAdmin from './pages/DocGenTemplatesAdmin';
import SuperAdmin, { SuperAdminTenantsTab, SuperAdminOrgsTab, SuperAdminUsersTab, SuperAdminMembershipsTab } from './pages/SuperAdmin';
import { SuperAdminBillingTab } from './pages/SuperAdminBillingTab';
import { SuperAdminJobsTab } from './pages/SuperAdminJobsTab';
import TeamManagement from './components/TeamManagement';
import Login from './pages/Login';
import AboutPage from './pages/AboutPage';
import HelpPage from './pages/HelpPage';
import ProtectedAdminRoute from './components/ProtectedRoute';
import SettingsIndexRedirect from './components/SettingsIndexRedirect';
import ProtectedSuperAdminRoute from './components/ProtectedSuperAdminRoute';
import ViewAsPage from './pages/ViewAsPage';
import DocumentViewer from './pages/DocumentViewer';
import SimulationBanner from './components/SimulationBanner';
import GlobalSearch from './components/GlobalSearch';
import { SideNavProvider, useSideNav } from './contexts/SideNavContext';
import { getSimulate, SIMULATE_CHANGED_EVENT } from './services/simulate';
import ChangePasswordModal from './components/ChangePasswordModal';
import RecordingProvider from './components/RecordingProvider';
import Avatar from './components/Avatar';
import Profile from './pages/Profile';
import api from './services/api';
import { isAuthenticated, getUser, clearAuth } from './services/auth';
import { hasPermission, canUseViewAs } from './utils/permissions';
import './App.css';

// Lists dropdown: directory (Providers, Payees, Contacts, School Districts) + org-wide lists (AT Packets, File Reviews, etc.). Items are permission/feature-gated.
function ListsDropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const [listItems, setListItems] = useState([]);
  const { tenantPath } = useTenantFromUrl();
  const dropdownRef = useRef(null);

  const fetchListItems = useCallback(() => {
    api.get('/whoami')
      .then((res) => {
        const features = res.data?.features || {};
        // Use effective permissions (simulated when "View as" is on) so the dropdown reflects the viewed-as role
        // Org-wide list links only for roles that may see everyone's data (view_all / transfers.view).
        // Training list: show for anyone who can use the page (read/manage own or others; monitor adds staff filter).
        const items = [
          { to: tenantPath('/lists/providers'), label: 'Providers' },
          { to: tenantPath('/lists/payees'), label: 'Payees' },
          { to: tenantPath('/lists/contacts'), label: 'Contacts' },
          { to: tenantPath('/lists/schools'), label: 'School Districts' },
          ...(features.at_packet && hasPermission('at_packets.view_all') ? [{ to: tenantPath('/lists/at-packets'), label: 'AT Packets' }] : []),
          ...(features.support_cases && hasPermission('support_cases.view_all') ? [{ to: tenantPath('/lists/support-cases'), label: 'Support Cases' }] : []),
          ...(features.file_review && hasPermission('file_reviews.view_all') ? [{ to: tenantPath('/lists/file-reviews'), label: 'File Reviews' }] : []),
          ...(features.training
            && (hasPermission('training.read') || hasPermission('training.manage') || hasPermission('training.monitor'))
            ? [{ to: tenantPath('/lists/training'), label: 'Training' }]
            : []),
          ...(hasPermission('transfers.view') ? [{ to: tenantPath('/lists/transfers'), label: 'Transfers' }] : []),
          ...(features.checklist && hasPermission('checklists.view_all') ? [{ to: tenantPath('/lists/my-checklists'), label: 'Checklists' }] : []),
          ...(features.client_services_provider_search && hasPermission('client_services.view_all') ? [{ to: tenantPath('/lists/client-services'), label: 'Service Needs' }] : []),
          ...(features.monthly_contact && hasPermission('monthly_contacts.view_all') ? [{ to: tenantPath('/lists/monthly-contact'), label: 'Monthly Contact' }] : []),
          ...(features.data_corrections && hasPermission('data_corrections.read') ? [{ to: tenantPath('/lists/data-corrections'), label: 'Data Corrections' }] : []),
          ...(hasPermission('clients.assign_staff') ? [{ to: tenantPath('/lists/waiting-assignment'), label: 'SC Assignment' }] : []),
          ...(hasPermission('caseload.manage_acting_sc_coverage')
            ? [{ to: tenantPath('/lists/acting-sc-coverage'), label: 'SC Coverage' }]
            : []),
          ...(hasPermission('notify_staff.send') ? [{ to: tenantPath('/lists/notify-staff'), label: 'Notify staff' }] : []),
          ...(hasPermission('mailing_list_builder.use')
            ? [{ to: tenantPath('/lists/mailing-list-builder'), label: 'Mailing List Builder' }]
            : []),
          ...((hasPermission('clients.create') || hasPermission('referrals.create')) ? [{ to: tenantPath('/lists/create-client-referral'), label: 'New Client / Referral' }] : []),
          ...(hasPermission('referrals.read') ? [{ to: tenantPath('/lists/incomplete-to-cornerstone'), label: 'Incomplete → Cornerstone' }] : []),
        ];
        setListItems(items);
      })
      .catch(() => setListItems([
        { to: tenantPath('/lists/providers'), label: 'Providers' },
        { to: tenantPath('/lists/payees'), label: 'Payees' },
        { to: tenantPath('/lists/contacts'), label: 'Contacts' },
        { to: tenantPath('/lists/schools'), label: 'School Districts' },
        ...(hasPermission('notify_staff.send') ? [{ to: tenantPath('/lists/notify-staff'), label: 'Notify staff' }] : []),
        ...(hasPermission('mailing_list_builder.use')
          ? [{ to: tenantPath('/lists/mailing-list-builder'), label: 'Mailing List Builder' }]
          : []),
      ]));
  }, [tenantPath]);

  useEffect(() => {
    fetchListItems();
  }, [fetchListItems]);

  useEffect(() => {
    const handler = () => fetchListItems();
    window.addEventListener(SIMULATE_CHANGED_EVENT, handler);
    return () => window.removeEventListener(SIMULATE_CHANGED_EVENT, handler);
  }, [fetchListItems]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onPointerDown = (e) => {
      if (!dropdownRef.current) return;
      if (!dropdownRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [isOpen]);

  const items = listItems.length > 0 ? listItems : [
    { to: tenantPath('/lists/providers'), label: 'Providers' },
    { to: tenantPath('/lists/payees'), label: 'Payees' },
    { to: tenantPath('/lists/contacts'), label: 'Contacts' },
    { to: tenantPath('/lists/schools'), label: 'School Districts' },
    ...(hasPermission('notify_staff.send') ? [{ to: tenantPath('/lists/notify-staff'), label: 'Notify staff' }] : []),
    ...(hasPermission('mailing_list_builder.use')
      ? [{ to: tenantPath('/lists/mailing-list-builder'), label: 'Mailing List Builder' }]
      : []),
  ];

  return (
    <div className={`nav-dropdown ${isOpen ? 'open' : ''}`} ref={dropdownRef}>
      <button
        type="button"
        className="nav-dropdown-toggle nav-lists-toggle"
        onClick={() => setIsOpen(!isOpen)}
        title="Lists (directory and org-wide lists)"
        aria-label="Lists"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <span className="nav-lists-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="5" cy="6" r="1.5" />
            <line x1="10" y1="6" x2="20" y2="6" />
            <circle cx="5" cy="12" r="1.5" />
            <line x1="10" y1="12" x2="20" y2="12" />
            <circle cx="5" cy="18" r="1.5" />
            <line x1="10" y1="18" x2="20" y2="18" />
          </svg>
        </span>
        <span className="dropdown-arrow">▼</span>
      </button>
      {isOpen && (
        <div className="nav-dropdown-menu nav-lists-menu">
          {items.map(({ to, label }) => (
            <Link
              key={to}
              to={to}
              className="nav-dropdown-item nav-dropdown-link"
              onClick={() => setIsOpen(false)}
            >
              {label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

// User dropdown menu component
function UserDropdown({ onOpenChangePassword }) {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const userData = getUser();
  const username = userData?.user?.username || 'User';
  const { tenantPath } = useTenantFromUrl();
  const [supportEnabled, setSupportEnabled] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get('/whoami')
      .then((res) => {
        const f = res.data?.features || {};
        if (!cancelled) setSupportEnabled(f.support_cases === true && hasPermission('support_cases.read'));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const handleLogout = () => {
    clearAuth();
    setIsOpen(false);
    navigate('/login', { replace: true });
  };

  const handleChangePassword = () => {
    setIsOpen(false);
    onOpenChangePassword?.();
  };

  useEffect(() => {
    if (!isOpen) return undefined;
    const onPointerDown = (e) => {
      if (!dropdownRef.current) return;
      if (!dropdownRef.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [isOpen]);

  return (
    <div className={`nav-dropdown ${isOpen ? 'open' : ''}`} ref={dropdownRef}>
      <button 
        type="button"
        className="nav-dropdown-toggle nav-user-toggle"
        onClick={() => setIsOpen(!isOpen)}
        title={username}
        aria-label={`Account menu for ${username}`}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <span className="nav-user-icon" aria-hidden="true">
          <Avatar userId={userData?.user?.id} name={username} size={30} />
        </span>
        <span className="dropdown-arrow">▼</span>
      </button>
      {isOpen && (
        <div className="nav-dropdown-menu nav-user-menu">
          <div className="nav-dropdown-item nav-user-info">
            {userData?.user?.email && (
              <div className="nav-user-email">{userData.user.email}</div>
            )}
            {userData?.tenant?.name && (
              <div className="nav-user-tenant">{userData.tenant.name}</div>
            )}
          </div>
          <div className="nav-dropdown-divider"></div>
          <Link
            to={tenantPath('/profile')}
            className="nav-dropdown-item nav-dropdown-link"
            onClick={() => setIsOpen(false)}
          >
            Profile
          </Link>
          {supportEnabled && (
            <Link
              to={tenantPath('/lists/support-cases')}
              className="nav-dropdown-item nav-dropdown-link"
              onClick={() => setIsOpen(false)}
            >
              Help &amp; support
            </Link>
          )}
          {onOpenChangePassword && (
            <button 
              type="button"
              className="nav-dropdown-item nav-dropdown-button"
              onClick={handleChangePassword}
            >
              Change password
            </button>
          )}
          <button 
            className="nav-dropdown-item nav-dropdown-button"
            onClick={handleLogout}
          >
            Logout
          </button>
        </div>
      )}
    </div>
  );
}

// Link to View As page (replaces dropdown for developers or users with settings.view_as)
function ViewAsLink() {
  const { tenantPath } = useTenantFromUrl();
  const sim = getSimulate();
  if (!canUseViewAs()) return null;
  const simLabel = sim?.userName ?? sim?.roleName ?? '';
  return (
    <Link to={tenantPath('/view-as')} className="nav-link view-as-nav-link" title="View app as another role or user (testing)">
      View as{simLabel ? `: ${simLabel}` : ''}
    </Link>
  );
}

// Paths where the layout "takes over" both panels (provider edit, contact, payee, school, referral) and the main left nav is replaced. On these, hamburger = go back to main menu (Lists hub). Client edit is under MainLayout so it keeps the main left nav.
function isDetailPageWithContextNav(pathname) {
  if (!pathname) return false;
  // Referral routes nested under /client/:id/... use ClientLayout + main nav — not standalone referral chrome.
  if (/\/client\/[^/]+\/referral\//.test(pathname)) return false;
  return /\/provider\/[^/]+/.test(pathname) || /\/contact\/[^/]+/.test(pathname) || /\/payee\/[^/]+/.test(pathname) || /\/school\/[^/]+/.test(pathname) || /\/referral\/[^/]+/.test(pathname);
}

// NavBar: Hamburger (toggle left nav only, no navigation) + Child Family Connect (navigate to hub) + tenant badge, then search, settings, user.
function NavBar({ databaseLabel, onOpenChangePassword }) {
  const userData = getUser();
  const location = useLocation();
  const navigate = useNavigate();
  const { tenantCode, tenantPath } = useTenantFromUrl();
  const { collapsed, toggleNav, expandNav } = useSideNav();
  const displayTenantCode = isAuthenticated() && (tenantCode || userData?.tenant?.code)
    ? (tenantCode || userData.tenant.code)
    : null;

  const onDetailPage = isDetailPageWithContextNav(location.pathname);
  // On client/provider/contact etc. edit pages the main left nav is replaced; hamburger navigates to Lists hub so the main menu (My Clients, Dashboard, etc.) comes back.
  const onHamburgerClick = onDetailPage
    ? () => {
        expandNav();
        navigate(tenantPath('/lists'));
      }
    : () => (collapsed ? expandNav() : toggleNav());

  return (
    <nav className="navbar">
      <div className="nav-container">
        {isAuthenticated() && (
          <button
            type="button"
            className="nav-menu-toggle"
            onClick={onHamburgerClick}
            aria-label={onDetailPage ? 'Back to main menu' : (collapsed ? 'Open menu' : 'Close menu')}
            aria-expanded={onDetailPage ? undefined : !collapsed}
            title={onDetailPage ? 'Back to main menu (Lists)' : (collapsed ? 'Open menu' : 'Close menu')}
          >
            <span className="nav-menu-toggle-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </span>
          </button>
        )}
        <Link to={displayTenantCode ? tenantPath('/lists') : '/'} className="nav-brand-wrap" aria-label={(userData?.tenant?.org_name || 'Child Family Connect') + ' home'}>
          <span className="nav-brand-text">{userData?.tenant?.org_name || 'Child Family Connect'}</span>
          {(displayTenantCode || (process.env.NODE_ENV !== 'production' && databaseLabel)) && (
            <span className="nav-tenant-badge nav-brand-badge">{displayTenantCode}</span>
          )}
          {process.env.NODE_ENV !== 'production' && databaseLabel && (
            <span className="nav-db-badge nav-brand-badge" title={`Backend is using ${databaseLabel} database`}>[{databaseLabel}]</span>
          )}
        </Link>
        {isAuthenticated() && <GlobalSearch />}
        <div className="nav-links">
          {isAuthenticated() && (
            <>
              <ListsDropdown />
              {hasPermission('settings.view') && (
                <Link to={tenantPath('/settings')} className="nav-link nav-link-settings" title="Settings" aria-label="Settings">
                  <svg
                    className="nav-link-settings-icon"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="12" r="3" />
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06A1.65 1.65 0 0 0 15 19.4a1.65 1.65 0 0 0-1 .6 1.65 1.65 0 0 0-.33 1V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-.33-1 1.65 1.65 0 0 0-1-.6 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-.6-1 1.65 1.65 0 0 0-1-.33H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1-.33 1.65 1.65 0 0 0 .6-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-.6 1.65 1.65 0 0 0 .33-1V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 .33 1 1.65 1.65 0 0 0 1 .6 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9c.23.31.39.67.45 1.05.03.18.05.37.05.55s-.02.37-.05.55c-.06.38-.22.74-.45 1.05z" />
                  </svg>
                </Link>
              )}
            </>
          )}
          <div className="nav-user-area">
            {isAuthenticated() ? (
              <UserDropdown onOpenChangePassword={onOpenChangePassword} />
            ) : (
              <Link to="/login" className="nav-link">Login</Link>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
}

function App() {
  const [simVersion, setSimVersion] = React.useState(0);
  const [databaseLabel, setDatabaseLabel] = React.useState(null);
  const [changePasswordOpen, setChangePasswordOpen] = React.useState(false);

  // When simulation changes (e.g. from View As page), re-render so nav and banner update
  useEffect(() => {
    const handler = () => setSimVersion((v) => v + 1);
    window.addEventListener(SIMULATE_CHANGED_EVENT, handler);
    return () => window.removeEventListener(SIMULATE_CHANGED_EVENT, handler);
  }, []);

  // In development, show [Local] vs [Azure] in the nav: from /api/health if backend sends it, else from REACT_APP_API_URL (or localhost)
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return;
    const apiBase = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
    const isLocalOrigin = typeof window !== 'undefined' && window.location?.hostname === 'localhost';
    const fallbackLabel = (apiBase && apiBase.includes('azure')) ? 'Azure' : 'Local';
    fetch(`${apiBase}/health`)
      .then((res) => res.json())
      .then((data) => {
        setDatabaseLabel(data.databaseLabel || fallbackLabel);
      })
      .catch(() => {
        // When health fails (e.g. backend not running), if we're on localhost assume Local
        setDatabaseLabel(isLocalOrigin ? 'Local' : fallbackLabel);
      });
  }, []);

  return (
    <Router>
      <RecordingProvider>
      <div className="App">
        <SideNavProvider>
          <NavBar 
            databaseLabel={databaseLabel} 
            onOpenChangePassword={() => setChangePasswordOpen(true)} 
          />
          {/* Reserve vertical space equal to fixed navbar so main (e.g. school/contact/payee) never sits under it */}
          <div className="app-navbar-spacer" aria-hidden="true" />
          <ChangePasswordModal 
            isOpen={changePasswordOpen} 
            onClose={() => setChangePasswordOpen(false)} 
          />
          {isAuthenticated() && (
            <SimulationBanner onEnd={() => setSimVersion((v) => v + 1)} />
          )}
          <main className="main-content">
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/help" element={<HelpPage />} />
            <Route path="/" element={<RootRedirect />} />
            <Route path="/:tenantCode" element={<RequireAuth><TenantLayout /></RequireAuth>}>
              <Route index element={<Navigate to="dashboard" replace />} />
              <Route path="documents/view/:fileId" element={<DocumentViewer />} />
              <Route path="referral/:referralId/team" element={<TeamManagement />} />
              <Route path="payee/new" element={<PayeeForm />} />
              <Route path="payee/:id" element={<PayeeForm />} />
              <Route path="contact/new" element={<ContactForm />} />
              <Route path="contact/:id/related" element={<ContactRelatedClients />} />
              <Route path="contact/:id" element={<ContactForm />} />
              <Route path="school/new" element={<SchoolForm />} />
              <Route path="school/:id" element={<SchoolForm />} />
              <Route element={<MainLayout />}>
                <Route path="provider/new" element={<ProviderForm />} />
                <Route path="provider/:id" element={<ProviderForm />} />
                <Route path="client/new" element={<ClientForm />} />
                <Route path="client/:id" element={<ClientLayout />}>
                  <Route index element={<ClientForm />} />
                  <Route path="folder" element={<ClientDocumentFolder />} />
                  <Route path="documents" element={<ClientDocuments />} />
                  <Route path="documents-all" element={<ClientDocumentsAll />} />
                  <Route path="email" element={<ClientEmailTemplates />} />
                  <Route path="at-packets/new" element={<ClientAtPacketCreate />} />
                  <Route path="at-packets" element={<ClientAtPackets />} />
                  <Route path="file-reviews" element={<ClientFileReviews />} />
                  <Route path="client-services" element={<ClientServices />} />
                  <Route path="milestones" element={<ClientMilestones />} />
                  <Route path="team" element={<ClientTeam />} />
                  <Route path="checklists" element={<ClientChecklists />} />
                  <Route path="work-tasks" element={<ClientWorkTasks />} />
                  <Route path="referral/:referralId/insurance" element={<ClientReferralInsurancePage />} />
                  <Route path="referral/new" element={<ReferralForm />} />
                  <Route path="referral/:referralId" element={<ReferralForm />} />
                  <Route path="referrals" element={<ReferralList />} />
                  <Route path="contact/:contactId" element={<ContactForm />} />
                  <Route path="school/:schoolId" element={<SchoolForm />} />
                  <Route path="provider/:providerId" element={<ProviderForm />} />
                </Route>
                <Route path="dashboard" element={<WorkTasksDashboard />} />
                <Route path="dashboard/rules" element={<MilestoneRules />} />
                <Route path="work-tasks" element={<Navigate to="../dashboard" replace />} />
                <Route path="work-tasks/rules" element={<Navigate to="../dashboard/rules" replace />} />
                <Route path="profile" element={<Profile />} />
            <Route path="lists" element={<ListsLayout />}>
                  <Route index element={<Navigate to="clients" replace />} />
                  <Route path="clients" element={<ClientList />} />
                  <Route path="waiting-assignment" element={<ScAssignmentList />} />
                  <Route path="create-client-referral" element={<CreateClientReferral />} />
                  <Route path="providers" element={<ProviderList />} />
                  <Route path="providers/:id" element={<ProviderList />} />
                  <Route path="payees" element={<PayeeList />} />
                  <Route path="payees/:id" element={<PayeeList />} />
                  <Route path="contacts" element={<ContactList />} />
                  <Route path="contacts/:id" element={<ContactList />} />
                  <Route path="schools" element={<SchoolList />} />
                  <Route path="schools/:id" element={<SchoolList />} />
                  <Route path="at-packets" element={<AtPackets />} />
                  <Route path="at-packets/pending-receipt-items" element={<AtPacketsPendingReceipt />} />
                  <Route path="at-packets/new" element={<AtPacketCreate />} />
                  <Route path="at-packets/:id" element={<AtPacketDetail />} />
                  <Route path="support-cases" element={<SupportCases />} />
                  <Route path="support-cases/new" element={<SupportCaseCreate />} />
                  <Route path="support-cases/:id" element={<SupportCaseDetail />} />
                  <Route path="file-reviews" element={<FileReviewList />} />
                  <Route path="file-reviews/new" element={<FileReviewCreate />} />
                  <Route path="file-reviews/:id" element={<FileReviewDetail />} />
                  <Route path="transfers" element={<TransferList />} />
                  <Route path="transfers/new" element={<TransferDetail />} />
                  <Route path="transfers/:id" element={<TransferDetail />} />
                  <Route path="training" element={<TrainingList />} />
                  <Route path="data-corrections" element={<DataCorrectionsList />} />
                  <Route path="my-checklists" element={<MyChecklists />} />
                  <Route path="my-todos" element={<PersonalMyTodos />} />
                  <Route path="client-services" element={<ProviderSearch />} />
                  <Route path="monthly-contact" element={<MonthlyContact />} />
                  <Route path="acting-sc-coverage" element={<ActingScCoveragePage />} />
                  <Route path="notify-staff" element={<NotifyStaff />} />
                  <Route path="mailing-list-builder" element={<MailingListBuilder />} />
                  <Route path="incomplete-to-cornerstone" element={<IncompleteToCornerstone />} />
                </Route>
                <Route path="cornerstone/:referralId" element={<CornerstoneDetail />} />
                <Route path="referral/:id/checklists" element={<ReferralChecklists />} />
                <Route path="referral/new" element={<ReferralForm />} />
                <Route path="referral/:id/milestones" element={<ReferralMilestones />} />
                <Route path="referral/:id" element={<ReferralForm />} />
                <Route path="referrals/client/:clientId" element={<ReferralList />} />
                <Route path="documents" element={<DocGenLanding />} />
                <Route path="reports" element={<ReportsHub />} />
                <Route path="reports/monthly-contact" element={<Navigate to="../../lists/monthly-contact" replace />} />
                <Route path="reports/clients" element={<ReportPageWrapper><ClientsReport /></ReportPageWrapper>} />
                <Route path="reports/client-status" element={<ReportPageWrapper><ClientStatusQuickEdit /></ReportPageWrapper>} />
                <Route path="view-as" element={<ViewAsPage />} />
                <Route
                  path="settings"
                  element={
                    <ProtectedAdminRoute>
                      <SettingsLayout />
                    </ProtectedAdminRoute>
                  }
                >
                  <Route index element={<SettingsIndexRedirect />} />
                  <Route path="roles-permissions" element={<RolePermissionManager />} />
                  <Route path="tenant-settings" element={<TenantSettings />} />
                  <Route path="notification-templates" element={<NotificationTemplates />} />
                  <Route path="email-templates/merge-fields" element={<EmailTemplates />} />
                  <Route path="email-templates" element={<EmailTemplates />} />
                  <Route
                    path="automation-rules"
                    element={
                      <RequireSuperAdmin>
                        <AutomationRules />
                      </RequireSuperAdmin>
                    }
                  />
                  <Route path="document-templates/doc-gen-fields" element={<DocGenTemplatesAdmin />} />
                  <Route path="document-templates" element={<DocGenTemplatesAdmin />} />
                  <Route path="client-folder-template" element={<ClientFolderTemplate />} />
                  <Route path="reset-user-password" element={<ResetUserPassword />} />
                  <Route path="test-tools" element={<TestTools />} />
                  <Route path="checklist-templates" element={<ChecklistTemplates />} />
                  <Route path="checklist-templates/:typeId" element={<ChecklistTemplates />} />
                  <Route path="file-review-template" element={<FileReviewTemplate />} />
                  <Route path="team" element={<TenantTeam />} />
                  <Route path="billable-seats" element={<BillableSeatsPage />} />
                  <Route path=":tableId" element={<LookupTableManager />} />
                </Route>
                <Route
                  path="super-admin"
                  element={
                    <ProtectedSuperAdminRoute>
                      <SuperAdmin />
                    </ProtectedSuperAdminRoute>
                  }
                >
                  <Route index element={<Navigate to="tenants" replace />} />
                  <Route path="tenants" element={<SuperAdminTenantsTab />} />
                  <Route path="orgs" element={<SuperAdminOrgsTab />} />
                  <Route path="users" element={<SuperAdminUsersTab />} />
                  <Route path="memberships" element={<SuperAdminMembershipsTab />} />
                  <Route path="support-cases" element={<SupportConsoleTab />} />
                  <Route path="support-cases/:id" element={<SupportConsoleDetail />} />
                  <Route path="releases" element={<SuperAdminReleasesTab />} />
                  <Route path="billing" element={<SuperAdminBillingTab />} />
                  <Route path="jobs" element={<SuperAdminJobsTab />} />
                </Route>
              </Route>
            </Route>
            </Routes>
            <LegalFooter />
          </main>
        </SideNavProvider>
    </div>
      </RecordingProvider>
  </Router>
  );
}

export default App;
